import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    useTheme,
    alpha,
    CircularProgress,
    Chip,
    Stack,
    Divider,
    Button,
    TextField,
    Paper // Added Paper
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import GavelIcon from '@mui/icons-material/Gavel';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'; // New Icon
import CampaignIcon from '@mui/icons-material/Campaign'; // New Icon

// Assuming these exist in your project
import Header from '../components/Header';
import Footer from '../components/Footer';
import EnquirySection from '../components/EnquirySection';

// --- Types ---
interface CoeRecord {
    recordID: number;
    month: string;
    biddingNo: string;
    vehicleClass: string;
    quota: string;
    bidsSuccess: string;
    bidsReceived: string;
    premium: string;
}

interface ProcessedCategory {
    id: string;
    title: string;
    desc: string;
    currentPrice: number;
    prevPrice: number;
    quota: string;
    bids: string;
    historyData: number[];
    pqp: number | string;
}

interface CoeDashboardPageProps {
    mode: 'light' | 'dark';
    setMode: (mode: 'light' | 'dark') => void;
}

// --- Helper: Calculate PQP ---
const calculatePQP = (category: string, allRecords: CoeRecord[]) => {
    if (category === 'Category E') return 'N.A';

    const monthMap = new Map<string, number[]>();
    const catRecords = allRecords.filter(r => r.vehicleClass === category);

    catRecords.forEach(r => {
        if (!monthMap.has(r.month)) {
            monthMap.set(r.month, []);
        }
        monthMap.get(r.month)?.push(parseInt(r.premium));
    });

    const sortedMonths = Array.from(monthMap.keys()).sort().reverse();
    const monthsToAverage = category === 'Category C' ? 6 : 3;
    const relevantMonths = sortedMonths.slice(0, monthsToAverage);

    if (relevantMonths.length === 0) return 0;

    let totalOfMonthlyAverages = 0;

    relevantMonths.forEach(month => {
        const premiums = monthMap.get(month) || [];
        const monthlyAvg = premiums.reduce((a, b) => a + b, 0) / premiums.length;
        totalOfMonthlyAverages += monthlyAvg;
    });

    const finalPQP = totalOfMonthlyAverages / relevantMonths.length;
    return Math.ceil(finalPQP);
};

// --- Helper: Sparkline ---
const Sparkline = ({ data, color }: { data: number[]; color: string }) => {
    if (!data || data.length < 2) return null;

    const height = 60;
    const width = 250;
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;

    const points = data
        .map((val, index) => {
            const x = (index / (data.length - 1)) * width;
            const y = height - ((val - min) / range) * (height - 10) - 5;
            return `${x},${y}`;
        })
        .join(' ');

    return (
        <Box sx={{ width: '100%', height: height, overflow: 'hidden', mt: 2 }}>
            <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
                <defs>
                    <linearGradient id={`grad-${color}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity={0.2} />
                        <stop offset="100%" stopColor={color} stopOpacity={0} />
                    </linearGradient>
                </defs>
                <polygon fill={`url(#grad-${color})`} points={`0,${height} ${points} ${width},${height}`} />
                <polyline fill="none" stroke={color} strokeWidth="3" points={points} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        </Box>
    );
};

// --- Component: CoeCard ---
const CoeCard = ({ data }: { data: ProcessedCategory }) => {
    const theme = useTheme();
    const difference = data.currentPrice - data.prevPrice;
    const isUp = difference > 0;
    const trendColor = isUp ? theme.palette.error.main : theme.palette.success.main;
    const trendBg = isUp ? alpha(theme.palette.error.main, 0.1) : alpha(theme.palette.success.main, 0.1);
    const TrendIcon = isUp ? TrendingUpIcon : TrendingDownIcon;

    const fmt = (val: number | string) => {
        if (typeof val === 'string') return val;
        return new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD', maximumFractionDigits: 0 }).format(val);
    };

    return (
        <Card
            elevation={3}
            sx={{
                height: '100%',
                borderRadius: 3,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: theme.shadows[10],
                }
            }}
        >
            <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h6" fontWeight="800" sx={{ lineHeight: 1 }}>{data.title}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.5 }}>{data.desc}</Typography>
                </Box>
            </Box>

            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', pt: 3 }}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="600" gutterBottom>CURRENT PREMIUM</Typography>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Typography variant="h5" fontWeight="900" color="text.primary">{fmt(data.currentPrice)}</Typography>
                        <Chip
                            icon={<TrendIcon style={{ color: trendColor }} />}
                            label={fmt(Math.abs(difference))}
                            sx={{
                                bgcolor: trendBg,
                                color: trendColor,
                                fontWeight: 'bold',
                                borderRadius: 1,
                                height: 'auto',
                                py: 0.5,
                                px: 0.5,
                                flexShrink: 0,
                                '& .MuiChip-label': { paddingLeft: 1, paddingRight: 1 }
                            }}
                        />
                    </Stack>
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Box sx={{ mb: 'auto', mt: 2 }}>
                    <Grid container spacing={0}>
                        <Grid size={{ xs: 4 }}>
                            <Stack spacing={0.5} alignItems="center">
                                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ opacity: 0.7 }}>
                                    <DirectionsCarIcon sx={{ fontSize: 16 }} />
                                    <Typography variant="caption" fontWeight="bold">QUOTA</Typography>
                                </Stack>
                                <Typography variant="h6" fontWeight="700">{data.quota}</Typography>
                            </Stack>
                        </Grid>
                        <Grid size={{ xs: 4 }} sx={{ borderLeft: `1px solid ${theme.palette.divider}`, borderRight: `1px solid ${theme.palette.divider}` }}>
                            <Stack spacing={0.5} alignItems="center">
                                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ opacity: 0.7 }}>
                                    <GavelIcon sx={{ fontSize: 16 }} />
                                    <Typography variant="caption" fontWeight="bold">BIDS</Typography>
                                </Stack>
                                <Typography variant="h6" fontWeight="700">{data.bids}</Typography>
                            </Stack>
                        </Grid>
                        <Grid size={{ xs: 4 }}>
                            <Stack spacing={0.5} alignItems="center">
                                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ opacity: 0.7 }}>
                                    <MonetizationOnIcon sx={{ fontSize: 16 }} />
                                    <Typography variant="caption" fontWeight="bold">PQP</Typography>
                                </Stack>
                                <Typography variant="h6" fontWeight="700">
                                    {typeof data.pqp === 'number' ? `$${(data.pqp / 1000).toFixed(1)}k` : data.pqp}
                                </Typography>
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>

                <Box sx={{ mt: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="text.secondary" fontWeight="600">3-MONTH TREND</Typography>
                        <Button size="small" sx={{ fontSize: 10, minWidth: 'auto', p: 0 }}>FULL HISTORY</Button>
                    </Stack>
                    <Sparkline data={data.historyData} color={theme.palette.primary.main} />
                </Box>
            </CardContent>
        </Card>
    );
};

// --- Main Page Component ---
const CoeDashboardPage = ({ mode, setMode }: CoeDashboardPageProps) => {
    const theme = useTheme();
    const [rawCoeList, setRawCoeList] = useState<CoeRecord[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchCOEData = async () => {
            setLoading(true);
            try {
                const response = await axios.get<any>(
                    'https://api.workhorse.com.sg/Vehicle/VehicleApiCall',
                    { headers: { 'Content-Type': 'application/json', 'Api-Key': 'FrontEndKey' } }
                );
                const rawList = response?.data?.responseData?.vehicleApiCallList || [];
                setRawCoeList(rawList);
            } catch (err) {
                console.error(err);
                setError("Unable to load COE results.");
            } finally {
                setLoading(false);
            }
        };
        fetchCOEData();
    }, []);

    // Extract Latest Bidding Info for the Banner
    const latestBiddingInfo = useMemo(() => {
        if (!rawCoeList || rawCoeList.length === 0) return null;
        // Sort specifically to get the absolute latest across all categories
        const sorted = [...rawCoeList].sort((a, b) => {
            if (a.month !== b.month) return b.month.localeCompare(a.month);
            return b.biddingNo.localeCompare(a.biddingNo);
        });
        return sorted[0]; // Returns the latest record found
    }, [rawCoeList]);

    const processedData = useMemo(() => {
        if (!rawCoeList || rawCoeList.length === 0) return {};

        const categories = [
            { id: 'Category A', title: 'CAT A', desc: 'Cars ≤ 1600cc & 130bhp' },
            { id: 'Category B', title: 'CAT B', desc: 'Cars > 1600cc or 130bhp' },
            { id: 'Category C', title: 'CAT C', desc: 'Goods Vehicles & Buses' },
            { id: 'Category E', title: 'CAT E', desc: 'Open Category' },
        ];

        const result: Record<string, ProcessedCategory> = {};

        categories.forEach(cat => {
            const records = rawCoeList.filter(r => r.vehicleClass === cat.id);
            records.sort((a, b) => {
                if (a.month !== b.month) return b.month.localeCompare(a.month);
                return b.biddingNo.localeCompare(a.biddingNo);
            });

            if (records.length > 0) {
                const current = records[0];
                const previous = records.length > 1 ? records[1] : current;
                const history = records.slice(0, 6).map(r => parseInt(r.premium)).reverse();
                const calculatedPqp = calculatePQP(cat.id, rawCoeList);

                result[cat.id] = {
                    ...cat,
                    currentPrice: parseInt(current.premium),
                    prevPrice: parseInt(previous.premium),
                    quota: current.quota,
                    bids: current.bidsReceived,
                    historyData: history,
                    pqp: calculatedPqp
                };
            }
        });
        return result;
    }, [rawCoeList]);

    // ---------------------------------------------
    // HELPER: Calculate Next Tender Date
    // ---------------------------------------------
    // ---------------------------------------------
    // HELPER: Calculate Next Tender Date
    // ---------------------------------------------
    const getNextTenderDate = () => {
        // Safety check in case data isn't loaded yet
        if (!latestBiddingInfo?.month || !latestBiddingInfo?.biddingNo) return "Loading...";

        const currentMonthStr = latestBiddingInfo.month; // e.g., "November"

        // Check if it is 1st Bidding
        const isFirstBidding = latestBiddingInfo.biddingNo === '1';

        // CASE 1: Currently 1st Bidding -> Next is 17th of SAME Month
        // Example: 1st Dec -> 17 Dec
        if (isFirstBidding) {
            return `17 ${currentMonthStr}`;
        }

        // CASE 2: Currently 2nd Bidding -> Next is 1st of NEXT Month
        // Example: 2nd Nov -> 1 Dec
        else {
            const months = [
                "January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"
            ];

            // Find index of current month (e.g., November is index 10)
            const currentIdx = months.findIndex(
                (m) => m.toLowerCase() === currentMonthStr.toLowerCase()
            );

            // Handle edge case where month name doesn't match
            if (currentIdx === -1) return "Coming Soon";

            // Calculate next month index (Modulus 12 handles December -> January)
            const nextIdx = (currentIdx + 1) % 12;
            const nextMonthName = months[nextIdx];

            return `1 ${nextMonthName}`;
        }
    };

    return (
        <>
            <Header />
            <Box sx={{ bgcolor: theme.palette.mode === 'light' ? '#f4f6f8' : theme.palette.background.default, minHeight: '100vh', py: 8, mt: 8 }}>
                <Container maxWidth="lg">
                    <Box textAlign="center" mb={4}>
                        <Typography variant="overline" color="primary" fontWeight="bold" letterSpacing={1.5}>Market Watch</Typography>
                        <Typography variant="h3" fontWeight="800" gutterBottom>LATEST COE PRICES</Typography>
                        <Typography variant="body1" color="text.secondary" maxWidth="600px" mx="auto">Accurate data to help you make smarter buying and selling decisions.</Typography>
                    </Box>

                    {/* ---------------- INFO BANNER SECTION ---------------- */}
                    {!loading && latestBiddingInfo && (
                        <Paper
                            elevation={0}
                            sx={{
                                mb: 6,
                                p: { xs: 2, md: 3 },
                                borderRadius: 3,
                                border: `1px solid ${theme.palette.divider}`,
                                bgcolor: theme.palette.background.paper,
                                maxWidth: '800px',
                                mx: 'auto'
                            }}
                        >
                            <Grid container spacing={2} alignItems="center" justifyContent="center">
                                {/* Left: Current Status */}
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Stack direction="row" spacing={2} alignItems="center" justifyContent={{ xs: 'center', md: 'flex-start' }}>
                                        <Box>
                                            <Typography variant="caption" display="block" color="text.secondary" fontWeight="bold" letterSpacing={1}>
                                                CURRENT RESULTS
                                            </Typography>
                                            <Typography variant="subtitle1" fontWeight="800">
                                                {latestBiddingInfo.month} <span style={{ opacity: 0.4 }}>|</span> {latestBiddingInfo.biddingNo === '1' ? '1st' : '2nd'} Bidding
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Grid>

                                {/* Divider for desktop */}
                                <Grid size={{ xs: 0 }} sx={{ display: { xs: 'none', md: 'block' } }}>
                                    <Divider orientation="vertical" flexItem sx={{ height: '40px', mx: 2 }} />
                                </Grid>

                                {/* Right: Next Tender Date */}
                                <Grid size={{ xs: 12, md: 5 }}>
                                    <Stack direction="row" spacing={2} alignItems="center" justifyContent={{ xs: 'center', md: 'flex-end' }}>
                                        <Box sx={{ textAlign: { xs: 'center', md: 'right' } }}>
                                            <Typography variant="caption" display="block" color="text.secondary" fontWeight="bold" letterSpacing={1}>
                                                NEXT TENDER
                                            </Typography>

                                            {/* CALL THE FUNCTION HERE */}
                                            <Typography variant="subtitle1" fontWeight="800" color="text.primary">
                                                {getNextTenderDate()}
                                            </Typography>

                                        </Box>
                                        <Box sx={{
                                            bgcolor: '#f3f4f6',
                                            color: '#64748b',
                                            p: 1.5,
                                            borderRadius: '50%',
                                            display: 'flex'
                                        }}>
                                            {/* Ensure you have this icon imported */}
                                            <CalendarMonthIcon />
                                        </Box>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Paper>
                    )}
                    {/* ----------------------------------------------------- */}

                    {loading && <Box display="flex" justifyContent="center" my={10}><CircularProgress /></Box>}
                    {error && <Typography color="error" align="center">{error}</Typography>}

                    {!loading && !error && (
                        <Grid container spacing={4}>
                            {processedData['Category A'] && <Grid size={{ xs: 12, md: 6, lg: 3 }}><CoeCard data={processedData['Category A']} /></Grid>}
                            {processedData['Category B'] && <Grid size={{ xs: 12, md: 6, lg: 3 }}><CoeCard data={processedData['Category B']} /></Grid>}
                            {processedData['Category C'] && <Grid size={{ xs: 12, md: 6, lg: 3 }}><CoeCard data={processedData['Category C']} /></Grid>}
                            {processedData['Category E'] && <Grid size={{ xs: 12, md: 6, lg: 3 }}><CoeCard data={processedData['Category E']} /></Grid>}
                        </Grid>
                    )}
                </Container>
            </Box>

            <EnquirySection />
            <Footer />
        </>
    );
}

export default CoeDashboardPage;